import {
  Table,
  TableBody,
  TableContainer,
  makeStyles,
} from '@material-ui/core';
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getOnHoverScrollbarStyle } from '../../styles/onHoverScrollbar';
import { useResponsiveMobile } from '../WbSidebar';
import { WbNoData } from '../WbNoData';
import { TableContent } from './contents/TableContent';
import {
  WbTableContextProvider,
  useWbTableContext,
} from './context/WbTableContext';
import { TableContentLoader } from './loaders/TableContentLoader';
import { CustomColumnDef, WbTableLoader, WbTableProps } from './types';
import { TableHeader } from './contents/TableHeader';
import { WbTablePagination } from '../WbTablePagination';
import { SELECTOR_COLUMN_WIDTH } from './constants';
import { ACTIONS_COLUMN_ID } from './StandardColumns';

const useStyles = makeStyles(
  theme => ({
    root: {
      tableLayout: 'fixed',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    paginationRoot: {
      position: 'relative',
      width: '100%',
    },
    paginationActions: {
      position: 'absolute',
      right: 0,
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    scrollbar: {
      ...getOnHoverScrollbarStyle(theme),
    },
  }),
  { name: 'WbTable' },
);

const getContainerAvailableWidth = (tableEl: HTMLElement): number => {
  const width = tableEl.getBoundingClientRect().width;

  return width;
};

function TableLoader({ loader, columnCount, rowCount = 10 }: WbTableLoader) {
  if (loader) {
    return loader;
  }
  if (columnCount) {
    return <TableContentLoader rows={rowCount} tableCells={columnCount} />;
  }
  return <></>;
}

function WbTableHeader<T>({
  styles,
  classNames,
}: Omit<WbTableProps<T>, 'pagination'>) {
  const { components, columns } = useWbTableContext<T>();
  if (components?.tableHeader) {
    return components.tableHeader;
  }

  if (columns.length) {
    return (
      <TableHeader
        className={classNames?.header}
        style={{ ...styles?.header }}
      />
    );
  }
  return <></>;
}

function WbTableContent<T>({ styles, classNames }: WbTableProps<T>) {
  const { columns, rows, components } = useWbTableContext<T>();
  if (components?.tableContent?.body) {
    return components.tableContent.body;
  }
  if (columns.length && rows.length) {
    return (
      <TableContent<T>
        rowClassName={classNames?.body}
        rowStyle={styles?.body}
      />
    );
  }
  return <></>;
}

export function WbTable<T>({
  components,
  noDataLabel,
  noDataComponent,
  pagination,
  styles,
  classNames,
  stickyHeader,
  selectedItems,
  selection,
  setSelectedItems,
  checkIfDisabled,
  tooltipIfDisabled,
  tableStyles,
  sorting: propsSorting,
  onSortingChange: propsOnSortingChange,
  manualSorting,
}: WbTableProps<T>) {
  const classes = useStyles();
  const isMobile = useResponsiveMobile();

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    pagination?.onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    pagination?.onRowsPerPageChange(parseInt(String(event.target.value), 10));
  };

  const MIN_COL_SIZE = 100;
  const MAX_COL_SIZE = Infinity;

  const [tableRef, setTableRef] = useState<HTMLDivElement | null>();
  const [sorting, setSorting] = useState<SortingState>(propsSorting || []);

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const resizableColumns: CustomColumnDef<T>[] = useMemo(() => {
    if (!components?.tableContent?.columns?.length) return [];
    return [
      ...components?.tableContent?.columns.map(c => {
        let size: number | undefined;
        let percentageWidth: string | undefined;

        const w = c.width || c?.cellProps?.width;

        const isPercentage = typeof w === 'string' && w.endsWith('%');
        const isPixel = typeof w === 'string' && w.endsWith('px');
        const isNumber = typeof w === 'number' || !isNaN(Number(w));

        if (isPixel) {
          const width = Number(w.replace(/\D/g, ''));
          if (width && !isNaN(width) && width !== Infinity) {
            size = width;
          }
        } else if (isNumber) {
          size = Number(w);
        }

        if (isPercentage) {
          percentageWidth = w;
        }

        const maxSize = c.maxWidth || MAX_COL_SIZE;
        let minSize = c.minWidth || MIN_COL_SIZE;

        if (size && size < minSize) {
          minSize = size;
        }

        const uniqueFieldId = `${
          c.uniqueFieldId || String(c.field).replaceAll('.', '_')
        }`;

        const resizable = c.resizable ?? true;

        const settings: CustomColumnDef<T> = {
          id: uniqueFieldId,
          accessorKey: c.field,
          header: () => c.headerName,
          footer: (props: any) => props.column.id,
          size: size,
          enableResizing: resizable,
          enableSorting: c?.sortable,
          maxSize: maxSize,
          minSize: minSize,
          originalWidth: percentageWidth || c?.cellProps?.width,
        };

        return settings;
      }),
    ];
  }, [MAX_COL_SIZE, components?.tableContent?.columns]);

  // Check if we should pin the actions column
  const shouldPinActionsColumn = useMemo(() => {
    if (!isMobile || !components?.tableContent?.columns?.length) return false;
    const lastColumn =
      components.tableContent.columns[
        components.tableContent.columns.length - 1
      ];
    return lastColumn?.uniqueFieldId === ACTIONS_COLUMN_ID;
  }, [isMobile, components?.tableContent?.columns]);

  // Configure column pinning state
  const columnPinning = useMemo(() => {
    if (!shouldPinActionsColumn || !resizableColumns?.length) return {};
    const lastColumnId = resizableColumns[resizableColumns.length - 1]?.id;
    return lastColumnId ? { right: [lastColumnId] } : {};
  }, [shouldPinActionsColumn, resizableColumns]);

  const tableSettings = useReactTable({
    data: components?.tableContent?.rows || [],
    columns: resizableColumns || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: propsOnSortingChange ?? setSorting,
    columnResizeMode: 'onChange',
    state: {
      sorting: propsSorting ?? sorting,
      columnPinning: columnPinning,
    },
    manualSorting: manualSorting,
  });

  const rightPinnedColumns = tableSettings.getState().columnPinning.right;

  // Calculate last non-pinned column index and reference
  const lastNonPinnedData = useMemo(() => {
    const columns = components?.tableContent?.columns || [];
    const rightPinned = new Set(rightPinnedColumns || []);
    const lastNonPinnedIndex = columns.reduce((lastIndex, c, idx) => {
      const colId = c.uniqueFieldId || String(c.field).replaceAll('.', '_');

      return !rightPinned.has(colId) ? idx : lastIndex;
    }, -1);

    let lastNonPinnedColumn = null;
    if (lastNonPinnedIndex !== -1) {
      const cell = columns[lastNonPinnedIndex];
      const uniqueFieldId =
        cell.uniqueFieldId || String(cell.field).replaceAll('.', '_');
      lastNonPinnedColumn = tableSettings.getColumn(uniqueFieldId);
    }

    return { lastNonPinnedIndex, lastNonPinnedColumn };
  }, [components?.tableContent?.columns, tableSettings, rightPinnedColumns]);

  const tableHeaders = tableSettings.getFlatHeaders();

  const columnSizing = tableSettings.getState().columnSizing;

  const columnSizingInfo = tableSettings.getState().columnSizingInfo;

  const columnSizeVars = useMemo(() => {
    let remainingWidth = containerWidth || 0;
    return tableHeaders.reduce((acc, column, index) => {
      const isLastNonPinned = index === lastNonPinnedData.lastNonPinnedIndex;

      // for last non-pinned column, fill remaining space
      const width = isLastNonPinned
        ? Math.max(remainingWidth, column.getSize())
        : column.getSize();

      remainingWidth -= width;

      // Assign the calculated size to CSS variables, to optimize performance and change column width without re-rendering individual cells
      acc[`--header-${column.id}-${index}-size`] = width;
      acc[`--col-${column.id}-${index}-size`] = width;
      return acc;
    }, {} as { [key: string]: number });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnSizingInfo,
    columnSizing,
    tableHeaders,
    containerWidth,
    lastNonPinnedData.lastNonPinnedIndex,
  ]);

  const calculateColumnWidths = useCallback(
    (tableEl: HTMLDivElement | null | undefined): void => {
      if (!tableEl) return;
      let tableWidth = getContainerAvailableWidth(tableEl);
      if (selection) tableWidth -= SELECTOR_COLUMN_WIDTH;
      setContainerWidth(tableWidth);

      // If user has manually resized a column, do not auto adjust widths

      let totalFixedWidth = 0;
      let totalColumnsWithFixedWidth = 0;

      const resizableColumnsClone = cloneDeep(resizableColumns);

      resizableColumnsClone.forEach(c => {
        if (c.size) {
          totalFixedWidth += c.size;
          totalColumnsWithFixedWidth++;
        }
      });

      resizableColumnsClone.forEach(c => {
        if (!c.size && c.originalWidth) {
          if (
            typeof c.originalWidth === 'string' &&
            c.originalWidth.endsWith('%')
          ) {
            const perc = Number(c.originalWidth.replace(/\D/g, ''));
            c.size = Math.floor((tableWidth / 100) * perc);
            totalFixedWidth += c.size;
            totalColumnsWithFixedWidth++;
          }
        }
      });

      const colWidth = Math.floor(
        Math.max(
          (tableWidth - totalFixedWidth) /
            Math.max(resizableColumns.length - totalColumnsWithFixedWidth, 1),
          0,
        ),
      );

      resizableColumns.forEach((column, index) => {
        let size: number;
        const cloneSize = resizableColumnsClone[index]?.size;

        if (cloneSize) {
          // Use the predefined size if available
          size = cloneSize;
        } else {
          // Default to calculated column width
          size = colWidth;
        }

        tableSettings.setColumnSizing(old => {
          return {
            ...old,
            [`${column.id}`]: Number(size),
          };
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resizableColumns],
  );

  const onTableMounted = useCallback(
    (el: HTMLDivElement | null | undefined) => {
      setTableRef(el);
      calculateColumnWidths(el);
    },
    [calculateColumnWidths],
  );

  useEffect(() => {
    let prevWidth: number;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.borderBoxSize?.[0].inlineSize;

        if (width !== prevWidth) {
          prevWidth = width;
          calculateColumnWidths(tableRef);
        }
      }
    });

    if (tableRef) {
      resizeObserver.observe(tableRef);
    }

    return () => {
      if (tableRef) {
        resizeObserver.unobserve(tableRef);
      }
    };
  }, [tableRef, calculateColumnWidths]);

  if (
    !components?.tableLoader?.loading &&
    !components?.tableContent?.body &&
    !components?.tableContent?.rows?.length
  ) {
    return noDataComponent || <WbNoData fillContainer text={noDataLabel} />;
  }

  return (
    <>
      <WbTableContextProvider
        tableSettings={tableSettings}
        components={components}
        selection={selection}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        checkIfDisabled={checkIfDisabled}
        tooltipIfDisabled={tooltipIfDisabled}
      >
        <TableContainer
          className={clsx(classes.scrollbar, classNames?.container)}
          style={{
            ...styles?.container,
            overflowX: 'auto',
          }}
        >
          {/* This div is used to measure the actual table available space, without padding and scrollbar from parent */}
          <div ref={onTableMounted}>
            <Table
              stickyHeader={stickyHeader}
              size="small"
              className={classes.root}
              style={{
                ...columnSizeVars,
                ...tableStyles,
              }}
            >
              <WbTableHeader styles={styles} classNames={classNames} />
              {components?.tableLoader?.loading ? (
                <TableBody>
                  <TableLoader
                    columnCount={components?.tableContent?.columns?.length}
                    {...components?.tableLoader}
                  />
                </TableBody>
              ) : (
                <WbTableContent styles={styles} classNames={classNames} />
              )}
            </Table>
          </div>
        </TableContainer>

        {!!pagination?.count && (
          <WbTablePagination
            rowsPerPageOptions={
              pagination.rowsPerPageOptions || [25, 50, 75, 100, 200]
            }
            component="div"
            count={pagination.count}
            rowsPerPage={pagination.limit}
            labelDisplayedRows={
              pagination.countlessOptions
                ? ({ from, to }) => `${from}-${to}`
                : undefined
            }
            nextIconButtonProps={
              pagination.countlessOptions
                ? {
                    disabled: !pagination.countlessOptions.hasNextPage(),
                  }
                : undefined
            }
            backIconButtonProps={
              pagination.countlessOptions
                ? {
                    disabled: pagination.countlessOptions.isFirstPage(),
                  }
                : undefined
            }
            page={
              'currentPage' in pagination
                ? pagination.currentPage
                : Math.floor(pagination.offset / pagination.limit)
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            style={{ alignSelf: 'flex-end', flexShrink: 0 }}
          />
        )}
      </WbTableContextProvider>
    </>
  );
}
